import React, {useState, useMemo} from 'react';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import {ModalAlert, useAlert} from '@/components/alerts';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import ConfigurationSettingsModal from './ConfigurationSettingsModal';
import useData from '@/utils/useData';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import SearchBox from '@/components/search/SearchBox';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {getConfigurationMetadata, isConfigurationBoolean, getConfigurationOptions} from '@/utils/configurationValidation';
import {extractErrorMessage} from '@/utils/errorHandling';

const CONFIG_I18N_KEYS: Record<string, {title?: string; description?: string}> = {
    SITENAME: {
        title: 'configuration_sitename_title',
        description: 'configuration_sitename_description',
    },
    FS_SESSION_EXPIRY_WARNING_TIME_IN_SECONDS: {
        title: 'configuration_session_expiry_warning_time_title',
        description: 'configuration_session_expiry_warning_time_description',
    },
    PERIOD_DEFAULT_LENGTH: {
        title: 'configuration_default_length',
        description: 'configuration_default_length_description',
    },
    MAX_CHART_LEGEND_LABELS_SHOWN: {
        title: 'configuration_chart_legend',
        description: 'configuration_chart_legend_description',
    },
    MAX_DATE_RANGE_DATA: {
        title: 'configuration_date_range',
        description: 'configuration_date_range_description',
    },
    WORDCLOUD_DEFAULT_MOST_COMMON_WORDS: {
        title: 'configuration_word_cloud',
        description: 'configuration_word_cloud_description',
    },
    RESERVED_EVALUATION_KEYS: {
        title: 'configuration_reserve_keys_title',
        description: 'configuration_reserve_keys_description',
    },
    BABEL_DEFAULT_LOCALE: {
        title: 'configuration_default_language',
        description: 'configuration_default_language_description',
    },
};

interface Configuration {
    id: number | null;
    key: string;
    title: string;
    value: string;
    description: string;
    createdOn: string | null;
    updatedOn?: string | null;
    source: 'database' | 'platform_default';
}


const ConfigurationListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData, config} = useOptimizedConfig();
    const {showSuccess, showError, showWarning} = useAlert();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [revertConfirmOpen, setRevertConfirmOpen] = useState<boolean>(false);
    const [configurationToRevert, setConfigurationToRevert] = useState<{id: number; title: string} | null>(null);
    const [isReverting, setIsReverting] = useState<boolean>(false);
    const [isReloading, setIsReloading] = useState<boolean>(false);

    const [allConfigurations, loadingConfigurations, storeAction] = useData(window.URLS.configurationsResource, {
        storeResolver: 'configurations',
    });

    const userCanAccessConfigurationResource = hasPermission('fs.system.configuration.read', userData?.permissions, true);
    const canChangeConfiguration = hasPermission('fs.system.configuration.update', userData?.permissions, true);
    const canDeleteConfiguration = hasPermission('fs.system.configuration.delete', userData?.permissions, true);

    const deleteAction = useStoreActions((actions: any) => actions.configurations.deleteOne);
    const setReloadConfigurations = useStoreActions((actions: any) => actions.configurations.setReload);

    const getDisplayValue = (config: Configuration): string => {
        const value = config.value || '';

        if (!value) {
            return '-';
        }

        const metadata = getConfigurationMetadata(config.key);

        if (isConfigurationBoolean(config.key)) {
            const boolValue = value.toLowerCase();
            if (boolValue === 'true' || boolValue === '1' || boolValue === 'yes' || boolValue === 'on') {
                return i18n.t('jt_true.text');
            }
            return i18n.t('jt_false.text');
        }

        const options = getConfigurationOptions(config.key);
        if (options && options.includes(value)) {
            return value.split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
        }

        return value;
    };

    const enhancedConfigurations = useMemo(() => {
        const configArray = Array.isArray(allConfigurations) ? allConfigurations : [];

        return configArray.map((config: Configuration) => {
            const mapping = CONFIG_I18N_KEYS[config.key] || {};

            const title = mapping.title ? i18n.t(`${mapping.title}.text`) : config.title;
            const description = mapping.description ? i18n.t(`${mapping.description}.text`) : config.description;

            return {
                ...config,
                title,
                description,
            };
        });
    }, [allConfigurations]);

    const filteredConfigurations = useMemo(() => {
        const configArray = enhancedConfigurations;

        if (!searchQuery.trim()) {
            return configArray;
        }

        const query = searchQuery.toLowerCase();
        return configArray.filter((config: Configuration) =>
            config.title.toLowerCase().includes(query) ||
            config.key.toLowerCase().includes(query) ||
            config.value.toLowerCase().includes(query) ||
            config.description.toLowerCase().includes(query)
        );
    }, [enhancedConfigurations, searchQuery]);

    const openSettingsModal = (configuration: Configuration): void => {
        setSelectedConfiguration(configuration);
        setSettingsModalOpen(true);
    };

    const reloadConfigurations = (): void => {
        // Trigger a reload using the setReload action from simpleCrudStoreFactory
        setReloadConfigurations(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setSelectedConfiguration(null);
        // Reload data when modal closes to show updated configurations
        reloadConfigurations();
    };

    const openRevertConfirm = (id: number, title: string): void => {
        setConfigurationToRevert({id, title});
        setRevertConfirmOpen(true);
    };

    const closeRevertConfirm = (): void => {
        setRevertConfirmOpen(false);
        setConfigurationToRevert(null);
        setIsReverting(false);
    };

    const confirmRevert = async (): Promise<void> => {
        if (!configurationToRevert) {return;}

        setIsReverting(true);
        try {
            const url = `${window.URLS.configurationsResource}/${configurationToRevert.id}`;
            await axios.delete(url);
            deleteAction(configurationToRevert.id);
            closeRevertConfirm();
            // Reload configurations to show the reverted state
            reloadConfigurations();
        } catch (error) {
            console.error('Failed to revert configuration:', error);
            setIsReverting(false);
        }
    };

    const reloadApplicationConfiguration = async (): Promise<void> => {
        setIsReloading(true);
        try {
            const response = await axios.post(`${window.URLS.configurationsResource}/reload`);
            const data = response.data;

            if (data.success) {
                const changeCount = Object.keys(data.changes).length;

                if (changeCount > 0) {
                    if (data.restart_required) {
                        showWarning(
                            i18n.t('jt_configuration_reloaded_with_changes_restart_required.text', {
                                changeCount,
                                configs: data.restart_required_configs.join(', ')
                            })
                        );
                    } else {
                        showSuccess(i18n.t('jt_configuration_reloaded_successfully_with_changes.text', {changeCount}));
                    }
                } else {
                    showSuccess(i18n.t('jt_configuration_reloaded_no_changes.text'));
                }

                reloadConfigurations();
                showSuccess(i18n.t('jt_page_will_reload_in_seconds.text'));
                // Refresh the page to ensure all workers have reloaded configuration
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                showError(i18n.t('jt_failed_to_reload_configuration.text', {message: data.message}));
            }
        } catch (error: any) {
            console.error('Failed to reload configuration:', error);
            const errorMessage = extractErrorMessage(error, i18n.t('jt_failed_to_reload_configuration.text', {message: ''}));
            showError(i18n.t('jt_failed_to_reload_configuration.text', {message: errorMessage}));
        } finally {
            setIsReloading(false);
        }
    };


    if (loadingUserData) {
        return <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>;
    }

    if (!userCanAccessConfigurationResource && !loadingUserData) {
        return (
            <>
                <ConditionalNav></ConditionalNav>
                <HelpTextBlock helpText={i18n.t('ht_manage_system_configuration.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const loadDbConfigs = config?.FCONST?.FS_LOAD_DATABASE_CONFIGURATIONS;
    const shouldShowDatabaseConfigurations = loadDbConfigs === true || loadDbConfigs === 'true';

    if (!shouldShowDatabaseConfigurations) {
        return (
            <>
                <ConditionalNav></ConditionalNav>
                <HelpTextBlock
                    helpText={i18n.t('ht_manage_system_configuration.text', {
                        defaultValue: 'Database-backed configuration is disabled for this environment.',
                    })}
                />
                <div className="alert alert-info" role="alert">
                    {i18n.t('jt_database_configuration_disabled.text', {
                        defaultValue: 'Database-backed configuration is disabled for this environment.',
                    })}
                </div>
            </>
        );
    }

    const columns = [
        {
            header: i18n.t('th_configuration_name.text'),
            accessorKey: 'title',
        },
        {
            header: i18n.t('jt_value.text'),
            accessorKey: 'value',
            cell: ({row}: {row: {original: Configuration}}) => {
                const displayValue = getDisplayValue(row.original);
                if (displayValue === '-') {return '-';}
                return displayValue.length > 50 ? `${displayValue.substring(0, 50)}...` : displayValue;
            },
        },
        {
            header: i18n.t('th_description.text'),
            accessorKey: 'description',
            cell: ({getValue}: {getValue: () => string}) => {
                const desc = getValue();
                if (!desc) {return '-';}
                return desc.length > 60 ? `${desc.substring(0, 60)}...` : desc;
            },
        },
        {
            header: i18n.t('th_source.text'),
            accessorKey: 'source',
            cell: ({getValue}: {getValue: () => string}) => {
                const source = getValue();
                return (
                    <span className={`badge ${source === 'database' ? 'badge-primary' : 'badge-secondary'}`}>
                        {source === 'database' ? i18n.t('jt_custom.text') : i18n.t('jt_default.text')}
                    </span>
                );
            },
        },
        {
            id: 'lastModified',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (row: Configuration) => {
                if (row.source === 'platform_default') {
                    return i18n.t('jt_not_available.text');
                }

                if (!row.updatedOn || row.updatedOn === '-') {
                    return configLoaded && row.createdOn ? formatDateTime(row.createdOn, true, {setZone: false}) : '';
                }
                return configLoaded ? formatDateTime(row.updatedOn, true, {setZone: false}) : '';
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: {row: {original: Configuration}}) => {
                const config = cell.row.original;
                const isPlatformDefault = config.source === 'platform_default';
                const canEdit = canChangeConfiguration;
                const canRevertToDefault = canDeleteConfiguration && !isPlatformDefault; // Can revert database configs to default

                return (
                    <div className="d-flex align-items-center justify-content-end w-100">
                        <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                            <Dropdown className="d-inline-block">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    id="settings-dropdown-1"
                                    data-cy="configuration-list-outlineinfo-btn"
                                />

                                <Dropdown.Menu align="end">
                                    {canEdit ? (
                                        <Dropdown.Item onClick={() => openSettingsModal(config)}>
                                            <Icon name="mdi-settings" className="mr-2" />
                                            {isPlatformDefault ? i18n.t('jt_override_default.text') : i18n.t('jt_settings.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')} placement="top">
                                            <div
                                                className="ml-auto"
                                                style={{
                                                    cursor: 'not-allowed',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled data-cy="configuration-list-btn">
                                                    <Icon name="mdi-settings" className="mr-2" />
                                                    {isPlatformDefault ? i18n.t('jt_override_default.text') : i18n.t('jt_settings.text')}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    )}

                                    {!isPlatformDefault && canDeleteConfiguration ? (
                                        <Dropdown.Item
                                            onClick={() => openRevertConfirm(config.id!, config.title)}
                                        >
                                            <Icon name="mdi-restore" className="mr-2" /> {i18n.t('jt_revert_to_default.text')}
                                        </Dropdown.Item>
                                    ) : null}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <ConditionalNav></ConditionalNav>

            <HelpTextBlock helpText={i18n.t('ht_manage_system_configuration_extended.text')} />

            <Row>
                <Col lg={3}>
                    <SearchBox
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={i18n.t('jt_search_configurations.text')}
                    />
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    <Button
                        variant="secondary"
                        size="sm"
                        className='mb-2'
                        icon="mdi-refresh"
                        onClick={reloadApplicationConfiguration}
                        loading={isReloading}
                        data-cy="reload-configuration-btn"
                    >
                        {i18n.t('jt_reload_configuration.text')}
                    </Button>
                </Col>
            </Row>

            {loadingConfigurations ? (
                <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
            ) : (
                <>
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={filteredConfigurations as Configuration[]} columns={columns} />
                        </div>
                    </div>

                    {settingsModalOpen ? (
                        <ConfigurationSettingsModal
                            modalOpen={settingsModalOpen}
                            closeModal={closeSettingsModal}
                            activeConfiguration={selectedConfiguration}
                        />
                    ) : null}

                    <ModalAlert
                        isOpen={revertConfirmOpen}
                        onClose={closeRevertConfirm}
                        variant="warning"
                        title={i18n.t('jt_revert_to_default.text')}
                        message={
                            configurationToRevert
                                ? i18n.t('jt_revert_configuration_confirm.text', {title: configurationToRevert.title})
                                : i18n.t('jt_revert_configuration_confirm_generic.text')
                        }
                        actions={[
                            {
                                label: i18n.t('jt_revert_to_default.text'),
                                onClick: confirmRevert,
                                variant: 'danger',
                                loading: isReverting,
                            }
                        ]}
                        dismissible={!isReverting}
                    />
                </>
            )}
        </>
    );
});

export default ConfigurationListView;

